import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Package } from "lucide-react";
import StatusBadge from "./StatusBadge";
import { formatCHF, formatKm, relativeTime, SIZE_LABEL, DELIVERY_TYPE_LABEL } from "@/lib/format";
import { formatAddress } from "@/lib/formatAddress";

export default function OrderCard({ order, to, action, testId }) {
  const sizeKey = order.package_size || order.size;
  const typeKey = order.delivery_type || order.urgency;
  const inner = (
    <div className="card-soft p-5 transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(0,0,0,0.06)] w-full overflow-hidden">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="overline">Ordine</p>
          <p className="font-display text-lg font-semibold tracking-tight truncate">
            #{order.id.slice(0, 8).toUpperCase()}
          </p>
          <p className="text-xs text-dapnow-muted mt-0.5 truncate">{relativeTime(order.created_at)}</p>
        </div>
        <div className="shrink-0">
          <StatusBadge order={order} audience="customer" />
        </div>
      </div>

      <div className="mt-4 space-y-2.5 text-sm min-w-0">
        <div className="flex items-start gap-2.5">
          <span className="mt-1 h-2 w-2 rounded-full bg-dapnow-blue ring-4 ring-dapnow-blue-soft shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="overline text-[10px]">Ritiro</p>
            <p className="text-dapnow-ink truncate">{formatAddress(order.pickup_address)}</p>
          </div>
        </div>
        <div className="ml-[3px] h-3 w-px bg-dapnow-line" />
        <div className="flex items-start gap-2.5">
          <span className="mt-1 h-2 w-2 rounded-full bg-dapnow-red ring-4 ring-red-100 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="overline text-[10px]">Consegna</p>
            <p className="text-dapnow-ink truncate">{formatAddress(order.delivery_address)}</p>
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-dapnow-muted">
        <span className="inline-flex items-center gap-1 shrink-0">
          <Package className="h-3.5 w-3.5" /> {SIZE_LABEL[sizeKey]?.split(" ")[0] || sizeKey}
        </span>
        <span className="inline-flex items-center gap-1 shrink-0">
          <MapPin className="h-3.5 w-3.5" /> {formatKm(order.distance_km)}
        </span>
        <span className="rounded-full bg-dapnow-blue-soft text-dapnow-blue px-2 py-0.5 font-semibold shrink-0">
          {DELIVERY_TYPE_LABEL[typeKey] || typeKey}
        </span>
      </div>

      <div className="mt-5 flex items-end justify-between gap-3">
        <div className="min-w-0">
          <p className="overline text-[10px]">Totale</p>
          <p className="font-display text-2xl font-bold tracking-tight truncate">{formatCHF(order.final_price ?? order.price)}</p>
        </div>
        {action || (
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-dapnow-blue shrink-0">
            Apri <ArrowRight className="h-4 w-4" />
          </span>
        )}
      </div>
    </div>
  );

  if (to) return (
    <Link to={to} data-testid={testId || `order-card-${order.id}`} className="block w-full min-w-0">
      {inner}
    </Link>
  );
  return <div data-testid={testId || `order-card-${order.id}`} className="w-full min-w-0">{inner}</div>;
}
